import { AlertBox, Box, Button, FlexBox, Icon, Typography } from '@vp/swan';
import { useChatActions, useChatMessages } from '../contexts/ChatContext';
import { AiAvatar } from './AiAvatar';
import { ChatErrorBanner } from './ChatErrorBanner';
import { GlobalChatBriefStyles } from './GlobalStyles';
import { LandingHero } from './LandingHero';
import { MessageBubble } from './MessageBubble';

interface ChatMessageListProps {
    onReview?: () => void;
}

export function ChatMessageList({ onReview }: ChatMessageListProps) {
    const {
        messages,
        isLoading,
        error,
        isStaleSession,
        isFatalError,
        retryCount,
        hasUserSent,
        messagesEndRef,
    } = useChatMessages();
    const { onRetry } = useChatActions();
    // Component-request messages are rendered in ChatInputArea, not as bubbles
    const filtered = messages.filter((msg) => msg.type !== 'componentRequest');
    // Hero stays visible until the user actually engages — a seeded greeting
    // alone shouldn't collapse it.
    const heroCollapsed = hasUserSent;

    return (
        <Box
            style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                backgroundColor: 'var(--swan-sem-color-bg-info)',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
            }}
            className="hide-scrollbar"
        >
            <Box
                role="log"
                aria-live="polite"
                aria-label="Chat messages"
                style={{
                    maxWidth: '768px',
                    margin: '0 auto',
                    paddingTop: 'var(--swan-sem-space-6)',
                    paddingBottom: 'var(--swan-sem-space-6)',
                    paddingLeft: 'var(--swan-sem-space-5)',
                    paddingRight: 'var(--swan-sem-space-5)',
                }}
            >
                {/* Landing hero — fades out once messages exist */}
                <Box
                    style={{
                        opacity: heroCollapsed ? 0 : 1,
                        transition: 'opacity 300ms ease-in-out',
                        pointerEvents: heroCollapsed ? 'none' : 'auto',
                        position: heroCollapsed ? 'absolute' : 'relative',
                        ...(heroCollapsed ? { height: 0, overflow: 'hidden' } : {}),
                    }}
                >
                    <GlobalChatBriefStyles />
                    <LandingHero />
                </Box>

                {filtered.map((message, index) => (
                    <MessageBubble
                        key={index}
                        message={message}
                        animate={index === filtered.length - 1 && message.role !== 'user'}
                    />
                ))}

                {isLoading && (
                    <FlexBox
                        justifyContent="flex-start"
                        style={{ marginBottom: 'var(--swan-sem-space-5)' }}
                        role="status"
                        aria-label="Assistant is thinking"
                    >
                        <AiAvatar animated />
                    </FlexBox>
                )}

                {!isLoading && isFatalError && error && (
                    <AlertBox
                        skin="error"
                        toast={false}
                        style={{ marginBottom: 'var(--swan-sem-space-5)' }}
                    >
                        <Typography>{error}</Typography>
                    </AlertBox>
                )}

                {!isLoading && isStaleSession && (
                    <AlertBox
                        skin="warning"
                        toast={false}
                        style={{ marginBottom: 'var(--swan-sem-space-5)' }}
                    >
                        <FlexBox alignItems="center" justifyContent="space-between">
                            <Typography>
                                Your chat session is out of date. Please refresh the page to
                                proceed.
                            </Typography>
                            <Button
                                skin="link"
                                iconPosition="left"
                                onClick={() => window.location.reload()}
                            >
                                <Icon iconType="reset" size="small" mr="3" />
                                Refresh page
                            </Button>
                        </FlexBox>
                    </AlertBox>
                )}

                {!isLoading && !isFatalError && !isStaleSession && error && (
                    <ChatErrorBanner
                        retryCount={retryCount}
                        onRetry={onRetry}
                        onReview={onReview}
                    />
                )}

                <div ref={messagesEndRef} />
            </Box>
        </Box>
    );
}
